import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { empresasApi } from '../../api/empresas'
import { usuariosManutencaoApi, UserManutencao } from '../../api/usuariosManutencao'
import { useAuth } from '../../contexts/AuthContext'
import { NivelAcesso } from '../../types/auth'
import { createLogger } from '../../utils/logger'

const logger = createLogger('VincularEmpresasDialog')

interface VincularEmpresasDialogProps {
  open: boolean
  onClose: () => void
  usuario: UserManutencao | null
}

const VincularEmpresasDialog: React.FC<VincularEmpresasDialogProps> = ({
  open,
  onClose,
  usuario,
}) => {
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  
  // Estado para rastrear qual empresa está sendo processada
  const [processingEmpresaId, setProcessingEmpresaId] = React.useState<number | null>(null)

  const isSuperAdmin = currentUser?.nivel_acesso === NivelAcesso.SUPER_ADMIN
  const isAdmin = currentUser?.nivel_acesso === NivelAcesso.ADMINISTRADOR

  // Buscar empresas disponíveis - IMPORTANTE: incluir inativas também
  // Isso garante que empresas inativas vinculadas ao usuário apareçam para desvincular
  const { data: empresas = [], isLoading: loadingEmpresas } = useQuery({
    queryKey: ['empresas', 'vincular', 'includeInactive'],
    queryFn: () => empresasApi.list(undefined, { includeInactive: true }),
    enabled: open,
  })

  // Buscar dados atualizados do usuário
  const { data: usuarioAtualizado, isLoading: loadingUsuario } = useQuery({
    queryKey: ['usuarios-manutencao', usuario?.id],
    queryFn: () => usuariosManutencaoApi.get(usuario?.id || 0),
    enabled: open && !!usuario?.id,
  })

  // IMPORTANTE: Garantir que empresas vinculadas ao usuário apareçam na lista,
  // mesmo que não estejam na lista geral (ex: empresas inativas ou de grupos diferentes)
  const empresasDisponiveisCompletas = React.useMemo(() => {
    // Se empresas ainda não carregou, retornar array vazio (será atualizado quando carregar)
    if (!empresas) return []
    
    // Criar um Map para busca rápida - sempre começar com empresas da lista geral
    const empresasMap = new Map((empresas || []).map(e => [e.id, e]))
    
    // IMPORTANTE: Usar usuarioAtualizado se disponível, senão usar usuario (fallback)
    // Isso garante que empresas vinculadas apareçam mesmo antes do usuarioAtualizado carregar
    const empresasVinculadas = (usuarioAtualizado?.empresas || usuario?.empresas || [])
    
    // Adicionar empresas vinculadas ao usuário que não estão na lista geral
    empresasVinculadas.forEach(emp => {
      if (!empresasMap.has(emp.id)) {
        // Empresa vinculada não está na lista geral - adicionar
        empresasMap.set(emp.id, {
          id: emp.id,
          nome: emp.nome,
          cnpj: undefined,
          grupo_id: emp.grupo_id || undefined,
          status: undefined,
        } as any)
      }
    })
    
    // Retornar array ordenado por nome
    return Array.from(empresasMap.values()).sort((a, b) => 
      (a.nome || '').localeCompare(b.nome || '')
    )
  }, [empresas, usuarioAtualizado, usuario])


  const vincularMutation = useMutation({
    mutationFn: ({ usuarioId, empresaId }: { usuarioId: number; empresaId: number }) =>
      usuariosManutencaoApi.vincularEmpresa(usuarioId, empresaId),
    onSuccess: async () => {
      // Invalidar e refetch imediatamente para garantir dados atualizados
      await queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao'] })
      await queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao', usuario?.id] })
      // Forçar refetch
      await queryClient.refetchQueries({ queryKey: ['usuarios-manutencao', usuario?.id] })
    },
  })

  const desvincularMutation = useMutation({
    mutationFn: ({ usuarioId, empresaId }: { usuarioId: number; empresaId: number }) =>
      usuariosManutencaoApi.desvincularEmpresa(usuarioId, empresaId),
    onSuccess: async (_, variables) => {
      logger.info('Desvinculamento bem-sucedido, invalidando cache', { empresaId: variables.empresaId })
      // Invalidar TODAS as queries relacionadas
      await queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao'] })
      await queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao', usuario?.id] })
      // Forçar refetch imediato com um pequeno delay para garantir que o backend processou
      await new Promise(resolve => setTimeout(resolve, 500))
      await queryClient.refetchQueries({ queryKey: ['usuarios-manutencao', usuario?.id] })
      logger.info('Cache invalidado e refetch concluído')
    },
    onError: (error: any) => {
      logger.error('Erro ao desvincular empresa', error)
      // Mesmo em caso de erro, invalidar cache para garantir dados atualizados
      queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao', usuario?.id] }).catch(err => {
        logger.error('Erro ao invalidar cache após erro de desvinculamento', err)
      })
    },
  })

  const handleToggleEmpresa = async (empresaId: number, event?: React.MouseEvent) => {
    // Prevenir propagação do evento para evitar conflitos
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (!usuario) {
      logger.warn('handleToggleEmpresa: usuário não definido')
      alert('Erro: usuário não definido')
      return
    }

    // Se já está processando esta empresa, ignorar
    if (processingEmpresaId === empresaId) {
      logger.debug('Empresa já está sendo processada, ignorando clique', empresaId)
      return
    }

    // Recalcular empresas vinculadas para ter dados atualizados
    const empresasVinculadasAtual = usuarioAtualizado?.empresas || usuario?.empresas || []
    const empresasVinculadasIdsAtual = empresasVinculadasAtual.map((e) => e.id)
    
    // Usar empresasVinculadasIds como fonte de verdade (vem do backend)
    const isSelected = empresasVinculadasIdsAtual.includes(empresaId)
    
    // Buscar nome da empresa das empresas disponíveis
    const empresaNome = empresasDisponiveisCompletas.find(e => e.id === empresaId)?.nome || 'desconhecida'
    
    logger.info('🔍 handleToggleEmpresa chamado', {
      empresaId,
      empresaNome,
      usuarioId: usuario.id,
      usuarioNome: usuario.name,
      isSelected,
      empresasVinculadasIds: empresasVinculadasIdsAtual,
      totalEmpresasVinculadas: empresasVinculadasAtual.length,
      empresasVinculadas: empresasVinculadasAtual.map(e => ({ id: e.id, nome: e.nome })),
    })
    
    // Marcar empresa como processando
    setProcessingEmpresaId(empresaId)
    
    try {
      if (isSelected) {
        // Desvincular
        const empresaNomeDesvincular = empresasDisponiveisCompletas.find(e => e.id === empresaId)?.nome || 'desconhecida'
        logger.info('🗑️ Iniciando desvinculamento de empresa', {
          empresaId,
          empresaNome: empresaNomeDesvincular,
          usuarioId: usuario.id,
        })
        await desvincularMutation.mutateAsync(
          { usuarioId: usuario.id, empresaId },
        )
        logger.info('✅ Empresa desvinculada com sucesso', { empresaId })
      } else {
        // Vincular
        const empresaNomeVincular = empresasDisponiveisCompletas.find(e => e.id === empresaId)?.nome || 'desconhecida'
        logger.info('➕ Iniciando vinculamento de empresa', {
          empresaId,
          empresaNome: empresaNomeVincular,
          usuarioId: usuario.id,
        })
        await vincularMutation.mutateAsync(
          { usuarioId: usuario.id, empresaId },
        )
        logger.info('✅ Empresa vinculada com sucesso', { empresaId })
      }
    } catch (error: any) {
      logger.error('❌ Erro na operação de toggle empresa', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Erro desconhecido'
      logger.error('📋 Detalhes do erro', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: errorMessage,
        empresaId,
        usuarioId: usuario.id,
      })
      alert(`Erro ao ${isSelected ? 'desvincular' : 'vincular'} empresa: ${errorMessage}`)
      throw error // Re-throw para que o React Query possa tratar
    } finally {
      // Limpar estado de processamento
      setProcessingEmpresaId(null)
    }
  }

  const handleClose = () => {
    // Limpar estado de processamento ao fechar
    setProcessingEmpresaId(null)
    onClose()
  }

  // Filtrar empresas baseado no nível de acesso
  // IMPORTANTE: Usar empresasDisponiveisCompletas que inclui empresas vinculadas ao usuário
  const empresasDisponiveis = React.useMemo(() => {
    return empresasDisponiveisCompletas.filter(() => {
      // Super admin vê todas
      if (isSuperAdmin) return true
      // Admin vê empresas dos grupos que administra
      if (isAdmin) {
        // NOTA: Filtro por grupos do admin será implementado quando necessário
        // Por enquanto, admin vê todas as empresas disponíveis
        return true
      }
      return true
    })
  }, [empresasDisponiveisCompletas, isSuperAdmin, isAdmin])

  const isLoading = loadingEmpresas || loadingUsuario || vincularMutation.isPending || desvincularMutation.isPending

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Vincular Empresas - {usuario?.name}
      </DialogTitle>
      <DialogContent>
        {isLoading && !empresasDisponiveis.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Selecione as empresas para vincular ao usuário:
            </Typography>
            <List>
              {empresasDisponiveis.map((empresa) => {
                // Usar empresasVinculadasIds como fonte de verdade (vem do backend)
                // Sempre recalcular baseado nos dados mais recentes
                const empresasVinculadasAtual = usuarioAtualizado?.empresas || usuario?.empresas || []
                const empresasVinculadasIdsAtual = empresasVinculadasAtual.map((e) => e.id)
                const isSelected = empresasVinculadasIdsAtual.includes(empresa.id)
                
                // Verificar se esta empresa específica está sendo processada
                const isProcessing = processingEmpresaId === empresa.id
                
                return (
                  <ListItem key={empresa.id} disablePadding>
                    <ListItemButton
                      onClick={async (e) => {
                        logger.info('🖱️ ListItemButton clicked', {
                          empresaId: empresa.id,
                          empresaNome: empresa.nome,
                          isSelected,
                          isLoading,
                          loadingEmpresas,
                          loadingUsuario,
                          isProcessing,
                          processingEmpresaId,
                          usuarioId: usuario?.id,
                          empresasVinculadasIds: (usuarioAtualizado?.empresas || usuario?.empresas || []).map(e => e.id),
                        })
                        
                        // Prevenir comportamento padrão e propagação
                        e.preventDefault()
                        e.stopPropagation()
                        
                        // Não permitir cliques se já está processando esta empresa ou geral
                        if (isProcessing || isLoading) {
                          logger.warn('⚠️ Clique ignorado - operação em andamento', {
                            isProcessing,
                            isLoading,
                            processingEmpresaId,
                          })
                          return
                        }
                        
                        try {
                          logger.info('🔄 Processando toggle para empresa', empresa.id)
                          await handleToggleEmpresa(empresa.id, e)
                        } catch (error) {
                          logger.error('❌ Erro ao processar clique no ListItemButton', error)
                          // Não mostrar alert duplicado (já é mostrado no handleToggleEmpresa)
                        }
                      }}
                      disabled={isProcessing || isLoading}
                      sx={{ 
                        cursor: (isProcessing || isLoading) ? 'wait' : 'pointer',
                        opacity: isProcessing ? 0.6 : 1,
                        '&:hover:not(:disabled)': {
                          backgroundColor: 'action.hover',
                        },
                        '&:disabled': {
                          cursor: 'wait',
                        },
                      }}
                    >
                      <ListItemIcon>
                        {isProcessing ? (
                          <CircularProgress size={20} sx={{ ml: 0.5 }} />
                        ) : (
                          <Checkbox
                            edge="start"
                            checked={isSelected}
                            tabIndex={-1}
                            disableRipple
                            readOnly
                            onClick={(e) => {
                              // Prevenir clique no checkbox, apenas no ListItemButton
                              e.preventDefault()
                              e.stopPropagation()
                            }}
                          />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={empresa.nome}
                        secondary={empresa.cnpj || 'Sem CNPJ'}
                      />
                      {isSelected && !isProcessing && (
                        <Chip label="Vinculada" size="small" color="primary" sx={{ ml: 1 }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
            {empresasDisponiveis.length === 0 && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                Nenhuma empresa disponível
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default VincularEmpresasDialog

